/**
 * Header component with unified UX pattern
 * Features:
 * - Lucide icons for all navigation items
 * - Icon-based cart with animated badge
 * - User avatar with dropdown menu (Gravatar)
 * - Modern theme toggle (Sun/Moon icons)
 * - Responsive mobile/desktop layouts
 * - Full accessibility support
 */

import { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  ShoppingCart,
  Sun,
  Moon,
  Menu,
  X,
  User,
  LayoutDashboard,
  LogOut,
  LogIn,
  UserPlus,
  HelpCircle,
  ShoppingBag,
  Package,
  ClipboardList,
  ChevronDown,
  BookOpen,
} from 'lucide-react';

// Context imports
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useCart } from '../../contexts/CartContext';

// Component imports
import CurrencySelector from './CurrencySelector';

// Utility imports
import { getGravatarUrl } from '../../utils/gravatar';
import { getUserRoleDisplay } from '../../utils/permissions';

// Hook imports
import { useClickOutside } from '../../hooks/useClickOutside';
import { useKeyboardNav } from '../../hooks/useKeyboardNav';
import { usePermission } from '../../hooks/usePermission';

export default function Header(): JSX.Element {
  // Context hooks
  const { currentUser, userData, signOut } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { itemCount } = useCart();
  const location = useLocation();

  // Permission hooks
  const { hasAnyAdminPermission } = usePermission();

  // State management
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [avatarDropdownOpen, setAvatarDropdownOpen] = useState(false);
  const [gravatarUrl, setGravatarUrl] = useState<string>('');

  // Refs for click-outside detection
  const avatarDropdownRef = useRef<HTMLDivElement>(null);

  /**
   * Generate Gravatar URL when user logs in
   */
  useEffect(() => {
    if (currentUser?.email) {
      setGravatarUrl(getGravatarUrl(currentUser.email));
    } else {
      setGravatarUrl('');
    }
  }, [currentUser?.email]);

  /**
   * Close dropdown when clicking outside
   */
  useClickOutside(avatarDropdownRef, () => setAvatarDropdownOpen(false));

  /**
   * Close dropdown when pressing Escape key
   */
  useKeyboardNav({
    onEscape: () => setAvatarDropdownOpen(false),
    enabled: avatarDropdownOpen,
  });

  /**
   * Close mobile menu when route changes
   */
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  /**
   * Handle user sign out
   */
  async function handleSignOut(): Promise<void> {
    try {
      setAvatarDropdownOpen(false);
      setMobileMenuOpen(false);
      await signOut();
    } catch (error) {
      console.error('Sign out error:', error);
    }
  }

  /**
   * Check if a path is active
   */
  const isActivePath = (path: string): boolean => {
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  /**
   * Get display name for user
   */
  const displayName =
    userData?.displayName ||
    currentUser?.email?.split('@')[0] ||
    'User';

  /**
   * Get user role badge text
   */
  const userRole = userData?.permissions
    ? getUserRoleDisplay(userData.permissions)
    : null;

  return (
    <header className="bg-white dark:bg-gray-900 shadow-sm sticky top-0 z-50 border-b border-gray-200 dark:border-gray-800">
      <nav className="container-custom">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link
            to="/"
            className="flex items-center gap-2 hover:opacity-80 transition-opacity"
            aria-label="Foxie Systems Home"
          >
            <div className="p-1.5 bg-primary-100 dark:bg-primary-900/30 rounded-lg">
              <BookOpen size={24} className="text-primary-600 dark:text-primary-400" />
            </div>
            <span className="hidden sm:inline text-xl font-bold text-gray-900 dark:text-gray-100">
              Foxie Systems
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-1">
            {/* Products Link */}
            <Link
              to="/products"
              className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                isActivePath('/products')
                  ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-400'
                  : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-gray-100'
              }`}
            >
              <ShoppingBag size={18} />
              <span>Products</span>
            </Link>

            {/* Help Link */}
            <Link
              to="/help"
              className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                isActivePath('/help')
                  ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-400'
                  : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-gray-100'
              }`}
            >
              <HelpCircle size={18} />
              <span>Help</span>
            </Link>

            {/* Divider */}
            <div className="w-px h-6 bg-gray-200 dark:bg-gray-700 mx-2" />

            {/* Theme Toggle */}
            <button
              onClick={toggleTheme}
              className="p-2 rounded-lg text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-gray-100 transition-colors"
              aria-label={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`}
            >
              {theme === 'dark' ? (
                <Sun size={20} />
              ) : (
                <Moon size={20} />
              )}
            </button>

            {/* Currency Selector */}
            <CurrencySelector />

            {/* Cart Icon with Badge */}
            <Link
              to="/cart"
              className={`relative p-2 rounded-lg transition-colors ${
                isActivePath('/cart')
                  ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-400'
                  : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-gray-100'
              }`}
              aria-label={`Shopping cart with ${itemCount} items`}
            >
              <ShoppingCart size={20} />
              {itemCount > 0 && (
                <span className="absolute -top-0.5 -right-0.5 bg-red-500 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center animate-pulse">
                  {itemCount > 99 ? '99+' : itemCount}
                </span>
              )}
            </Link>

            {/* Auth Section */}
            {currentUser && userData ? (
              /* Avatar with Dropdown Menu (Logged In) */
              <div className="relative ml-2" ref={avatarDropdownRef}>
                {/* Avatar Button */}
                <button
                  onClick={() => setAvatarDropdownOpen(!avatarDropdownOpen)}
                  className={`flex items-center gap-2 p-1.5 rounded-lg transition-colors ${
                    avatarDropdownOpen
                      ? 'bg-gray-100 dark:bg-gray-800'
                      : 'hover:bg-gray-100 dark:hover:bg-gray-800'
                  }`}
                  aria-label="User menu"
                  aria-expanded={avatarDropdownOpen}
                  aria-haspopup="true"
                >
                  {gravatarUrl ? (
                    <img
                      src={gravatarUrl}
                      alt=""
                      className="w-8 h-8 rounded-full border-2 border-gray-200 dark:border-gray-700"
                      loading="lazy"
                      onError={(e) => {
                        e.currentTarget.src = `https://www.gravatar.com/avatar/00000000000000000000000000000000?s=200&d=identicon`;
                      }}
                    />
                  ) : (
                    <div className="w-8 h-8 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center">
                      <User size={16} className="text-gray-500" />
                    </div>
                  )}
                  <ChevronDown size={16} className={`text-gray-500 transition-transform ${avatarDropdownOpen ? 'rotate-180' : ''}`} />
                </button>

                {/* Dropdown Menu */}
                {avatarDropdownOpen && (
                  <div
                    className="absolute right-0 mt-2 w-72 bg-white dark:bg-gray-800 rounded-xl shadow-xl border border-gray-200 dark:border-gray-700 overflow-hidden"
                    role="menu"
                  >
                    {/* User Info Section */}
                    <div className="px-4 py-4 bg-gray-50 dark:bg-gray-900/50 border-b border-gray-200 dark:border-gray-700">
                      <div className="flex items-center gap-3">
                        <img
                          src={gravatarUrl}
                          alt=""
                          className="w-12 h-12 rounded-full border-2 border-gray-200 dark:border-gray-700"
                          loading="lazy"
                        />
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-gray-900 dark:text-gray-100 truncate">
                            {displayName}
                          </p>
                          <p className="text-sm text-gray-500 dark:text-gray-400 truncate">
                            {currentUser.email}
                          </p>
                          {userRole && (
                            <span className="inline-flex items-center mt-1.5 px-2 py-0.5 bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-400 text-xs font-medium rounded-full">
                              {userRole}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Menu Items */}
                    <div className="py-2">
                      <Link
                        to="/user"
                        className="flex items-center gap-3 px-4 py-2.5 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                        onClick={() => setAvatarDropdownOpen(false)}
                        role="menuitem"
                      >
                        <User size={18} className="text-gray-500 dark:text-gray-400" />
                        <span>My Account</span>
                      </Link>
                      <Link
                        to="/user?tab=orders"
                        className="flex items-center gap-3 px-4 py-2.5 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                        onClick={() => setAvatarDropdownOpen(false)}
                        role="menuitem"
                      >
                        <ClipboardList size={18} className="text-gray-500 dark:text-gray-400" />
                        <span>My Orders</span>
                      </Link>
                      <Link
                        to="/user?tab=products"
                        className="flex items-center gap-3 px-4 py-2.5 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                        onClick={() => setAvatarDropdownOpen(false)}
                        role="menuitem"
                      >
                        <Package size={18} className="text-gray-500 dark:text-gray-400" />
                        <span>My Products</span>
                      </Link>
                      {hasAnyAdminPermission() && (
                        <>
                          <div className="my-2 border-t border-gray-200 dark:border-gray-700" />
                          <Link
                            to="/admin"
                            className="flex items-center gap-3 px-4 py-2.5 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                            onClick={() => setAvatarDropdownOpen(false)}
                            role="menuitem"
                          >
                            <LayoutDashboard size={18} className="text-gray-500 dark:text-gray-400" />
                            <span>Admin Dashboard</span>
                          </Link>
                        </>
                      )}
                    </div>

                    {/* Sign Out */}
                    <div className="border-t border-gray-200 dark:border-gray-700 py-2">
                      <button
                        onClick={handleSignOut}
                        className="flex items-center gap-3 w-full px-4 py-2.5 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                        role="menuitem"
                      >
                        <LogOut size={18} />
                        <span>Sign Out</span>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              /* Login/Signup Icon Buttons (Logged Out) */
              <div className="flex items-center gap-1 ml-2">
                <Link
                  to="/login"
                  className="p-2 rounded-lg text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-gray-100 transition-colors"
                  aria-label="Login"
                  title="Login"
                >
                  <LogIn size={20} />
                </Link>
                <Link
                  to="/register"
                  className="p-2 rounded-lg text-white bg-primary-600 hover:bg-primary-700 transition-colors"
                  aria-label="Sign Up"
                  title="Sign Up"
                >
                  <UserPlus size={20} />
                </Link>
              </div>
            )}
          </div>

          {/* Mobile: Cart + Menu Button */}
          <div className="flex items-center gap-2 md:hidden">
            {/* Cart Icon */}
            <Link
              to="/cart"
              className="relative p-2 rounded-lg text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              aria-label={`Shopping cart with ${itemCount} items`}
            >
              <ShoppingCart size={22} />
              {itemCount > 0 && (
                <span className="absolute -top-0.5 -right-0.5 bg-red-500 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center">
                  {itemCount > 99 ? '99+' : itemCount}
                </span>
              )}
            </Link>

            {/* Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="p-2 rounded-lg text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              aria-label="Toggle menu"
              aria-expanded={mobileMenuOpen}
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 space-y-1 border-t border-gray-200 dark:border-gray-800">
            {/* User Info Section (Logged In) */}
            {currentUser && userData && gravatarUrl && (
              <div className="flex items-center gap-3 px-3 py-3 mb-3 bg-gray-50 dark:bg-gray-800 rounded-xl">
                <img
                  src={gravatarUrl}
                  alt=""
                  className="w-12 h-12 rounded-full border-2 border-gray-200 dark:border-gray-700"
                  loading="lazy"
                />
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-gray-900 dark:text-gray-100 truncate">
                    {displayName}
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400 truncate">
                    {currentUser.email}
                  </p>
                  {userRole && (
                    <span className="inline-flex items-center mt-1 px-2 py-0.5 bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-400 text-xs font-medium rounded-full">
                      {userRole}
                    </span>
                  )}
                </div>
              </div>
            )}

            {/* Navigation Links */}
            <Link
              to="/products"
              className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${
                isActivePath('/products')
                  ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-400'
                  : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
              }`}
              onClick={() => setMobileMenuOpen(false)}
            >
              <ShoppingBag size={20} />
              <span>Products</span>
            </Link>

            {currentUser && (
              <>
                <Link
                  to="/user"
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${
                    isActivePath('/user')
                      ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-400'
                      : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
                  }`}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <User size={20} />
                  <span>My Account</span>
                </Link>
                <Link
                  to="/user?tab=orders"
                  className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <ClipboardList size={20} />
                  <span>My Orders</span>
                </Link>
                <Link
                  to="/user?tab=products"
                  className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <Package size={20} />
                  <span>My Products</span>
                </Link>
                {hasAnyAdminPermission() && (
                  <Link
                    to="/admin"
                    className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${
                      isActivePath('/admin')
                        ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-400'
                        : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
                    }`}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <LayoutDashboard size={20} />
                    <span>Admin Dashboard</span>
                  </Link>
                )}
              </>
            )}

            <Link
              to="/help"
              className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${
                isActivePath('/help')
                  ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-400'
                  : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
              }`}
              onClick={() => setMobileMenuOpen(false)}
            >
              <HelpCircle size={20} />
              <span>Help Center</span>
            </Link>

            {/* Divider */}
            <div className="my-2 border-t border-gray-200 dark:border-gray-700" />

            {/* Currency Selector (Mobile) */}
            <CurrencySelector compact />

            {/* Divider */}
            <div className="my-2 border-t border-gray-200 dark:border-gray-700" />

            {/* Theme Toggle */}
            <button
              onClick={toggleTheme}
              className="flex items-center gap-3 w-full px-3 py-2.5 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
            >
              {theme === 'dark' ? (
                <>
                  <Sun size={20} />
                  <span>Light Mode</span>
                </>
              ) : (
                <>
                  <Moon size={20} />
                  <span>Dark Mode</span>
                </>
              )}
            </button>

            {/* Auth Actions */}
            {currentUser ? (
              <button
                onClick={handleSignOut}
                className="flex items-center gap-3 w-full px-3 py-2.5 rounded-lg text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
              >
                <LogOut size={20} />
                <span>Sign Out</span>
              </button>
            ) : (
              <div className="grid grid-cols-2 gap-2 pt-2">
                <Link
                  to="/login"
                  className="flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 font-medium hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <LogIn size={18} />
                  Login
                </Link>
                <Link
                  to="/register"
                  className="flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-primary-600 text-white font-medium hover:bg-primary-700 transition-colors"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <UserPlus size={18} />
                  Sign Up
                </Link>
              </div>
            )}
          </div>
        )}
      </nav>

      {/* Screen Reader Live Region for Cart Updates */}
      <div role="status" aria-live="polite" className="sr-only">
        {itemCount > 0 && `${itemCount} items in cart`}
      </div>
    </header>
  );
}
